import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** Which real-time game the live match belongs to. */
export type ActiveMatchGame = 'qgammon' | 'qcp';

/** The one live match whose socket the app keeps connected across navigation. */
export interface ActiveMatchDescriptor {
  game: ActiveMatchGame;
  matchId: string;
  /** QGammon board orientation ('W'|'B'); undefined for QCP. */
  perspective?: 'W' | 'B';
  /** QCP practice flag; undefined/false otherwise. */
  vsBot?: boolean;
  /**
   * ADR-0114 — `waiting` = APPUNTAMENTO: i posti sono assegnati, **nessuno sta
   * giocando**, nessun orologio corre. `active` = si gioca. `undefined` = chi ha
   * aperto la partita non lo sapeva (coda ranked, pratica col bot: li' entrambi
   * hanno acconsentito ADESSO, e l'appuntamento non esiste).
   *
   * 🔴 Serve al PRIMO DISEGNO, e solo a quello: e' cio' che l'accettazione ha
   * letto nella risposta del server. L'autorita' resta `match:state` sul socket,
   * che porta lo stesso campo e lo aggiorna quando l'appuntamento diventa
   * partita — v. `lib/qgammon/matchStatus.ts`.
   */
  status?: 'waiting' | 'active';
  /** Epoch ms at start — for ordering/debug only (forfeit is server-side). */
  startedAt: number;
}

interface ActiveMatchState {
  current: ActiveMatchDescriptor | null;
  /**
   * Gli id QGammon delle partite di cui QUESTA sessione ha visto il `match:ended` (li scrive
   * `ActiveMatchProvider`, un attimo prima di `clearActiveMatch()`). Le leggono DUE schermi, con una
   * regola sola: la barra dell'azione persistente (`tavoliFiniti` in `azioniDelTorneo`) e la scheda
   * d'incontro (`useTavoliFiniti` → `tavoloDaAprireOf` con `finiti`, `facciaBottoneOf` con
   * `tavoloFinito`, #408). Un incontro Limulo resta «da giocare» sul server per ~45 s dopo la fine
   * della partita (prova Back_6, 26/09): ne' la barra ne' la scheda devono proporre di giocare un
   * tavolo appena chiuso.
   * In memoria (Redux), non persistita: si perde al reload. Con un tetto: e' un fatto visto, non uno
   * stato del server. ⛔ Non persisterla (revisione #403, punto 3): per una finestra di ~45 s porterebbe
   * i rischi dello storage nella WebView; dopo un reload la barra puo' riproporre il «gioca», e la
   * scheda d'incontro «Entra al tavolo», per il resto della finestra, e si accetta.
   */
  finite: string[];
}

/** Quante partite finite si ricordano: bastano le ultime, il resto l'ha gia' chiuso il server. */
export const PARTITE_FINITE_MAX = 20;

const initialState: ActiveMatchState = { current: null, finite: [] };

const activeMatchSlice = createSlice({
  name: 'activeMatch',
  initialState,
  reducers: {
    setActiveMatch(state, action: PayloadAction<ActiveMatchDescriptor>) {
      state.current = action.payload;
    },
    clearActiveMatch(state) {
      state.current = null;
    },
    partitaFinita(state, action: PayloadAction<string>) {
      if (state.finite.includes(action.payload)) return;
      state.finite = [...state.finite, action.payload].slice(-PARTITE_FINITE_MAX);
    },
    /**
     * Il colore dell'ack di `match:join` (board-ui 0.8.0, `myColor`) — per il colore alla RICARICA
     * durante la partita (reperto 3 del dossier TMA-orc, 27/09). Scrive SOLO se `current` e'
     * ANCORA quella partita: senza il confronto, un ack in arrivo mentre `match:ended` ha appena
     * svuotato lo slice (stesso render) resusciterebbe una partita gia' finita — e' per questo che
     * e' un'azione a se' e non un `setActiveMatch({...descriptor, perspective})` dal provider, che
     * chiuderebbe sulla `descriptor` catturata nella closure del render invece che sullo stato vivo.
     */
    coloreDalServer(state, action: PayloadAction<{ matchId: string; color: 'W' | 'B' }>) {
      if (state.current?.game !== 'qgammon' || state.current.matchId !== action.payload.matchId) return;
      state.current.perspective = action.payload.color;
    },
  },
});

export const { setActiveMatch, clearActiveMatch, partitaFinita, coloreDalServer } = activeMatchSlice.actions;
export default activeMatchSlice.reducer;
